import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase/client';
import { useAuth } from '@/providers/AuthProvider';
// ────────────────────────────────────────────────────────────────────────────
// Query keys
// ────────────────────────────────────────────────────────────────────────────
export const achKeys = {
    catalog: ['achievements', 'catalog'],
    player: (playerId) => ['achievements', 'player', playerId],
};
// ────────────────────────────────────────────────────────────────────────────
// Queries
// ────────────────────────────────────────────────────────────────────────────
/** Catalogue complet (lecture publique). */
export function useAchievements() {
    return useQuery({
        queryKey: achKeys.catalog,
        queryFn: async () => {
            const { data, error } = await supabase
                .from('achievements')
                .select('*')
                .order('rarity', { ascending: true })
                .order('name', { ascending: true });
            if (error)
                throw error;
            return (data ?? []);
        },
        staleTime: 10 * 60_000, // catalogue très statique
    });
}
/**
 * Liste les achievements d'un joueur (uniquement les débloqués pour les
 * autres ; pour soi-même on voit aussi les progressions intermédiaires).
 */
export function usePlayerAchievements(playerId) {
    return useQuery({
        queryKey: playerId ? achKeys.player(playerId) : ['achievements', 'player', 'none'],
        enabled: !!playerId,
        queryFn: async () => {
            if (!playerId)
                return [];
            const { data, error } = await supabase
                .from('player_achievements')
                .select('*')
                .eq('player_id', playerId)
                .order('unlocked_at', { ascending: false });
            if (error)
                throw error;
            return (data ?? []);
        },
        staleTime: 30_000,
    });
}
// ────────────────────────────────────────────────────────────────────────────
// Mutation : check_my_achievements (à appeler après une action déclenchante)
// ────────────────────────────────────────────────────────────────────────────
export function useCheckMyAchievements() {
    const qc = useQueryClient();
    const { user } = useAuth();
    return useMutation({
        mutationFn: async () => {
            const { data, error } = await supabase.rpc('check_my_achievements');
            if (error)
                throw error;
            return (data ?? []);
        },
        onSuccess: (newly) => {
            if (newly.length > 0 && user?.id) {
                void qc.invalidateQueries({ queryKey: achKeys.player(user.id) });
                void qc.invalidateQueries({ queryKey: ['profile', user.id] });
            }
        },
    });
}
